import { useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { ChevronRight, ChevronLeft, Check } from "lucide-react";

const STEPS = [
  "Customer Info", "School Info", "Domain & Email", "Branding",
  "School Pricing", "School Year", "Enrollment Fields", "Policies",
  "Documents", "Uploads", "Website Content",
  "Emails", "Administrators", "Review & Submit"
];

function Field({ label, helpText, children }) {
  return (
    <div className="mb-4">
      <Label className="text-sm font-medium text-[#1C2E4A] mb-1.5 block">{label}</Label>
      {children}
      {helpText && <p className="text-xs text-[#595959]/60 mt-1">{helpText}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", required }) {
  return <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="border-[#E5E2DC] bg-[#FAF9F6]" required={required} />;
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return <Textarea value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="border-[#E5E2DC] bg-[#FAF9F6] resize-none" />;
}

function HelpToggle({ label, checked, onChange }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <Checkbox checked={checked || false} onCheckedChange={onChange} />
      <span className="text-xs text-[#C8A97E]">{label}</span>
    </div>
  );
}

function SectionTitle({ title }) {
  return <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 pb-2 border-b border-[#E5E2DC]">{title}</h3>;
}

export default function IntakeFormSteps({ data, setData, onComplete }) {
  const [step, setStep] = useState(0);

  const u = (section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };

  const uArr = (section, index, field, value) => {
    setData(prev => {
      const arr = [...(prev[section] || [])];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [section]: arr };
    });
  };

  const addToArr = (section, template) => {
    setData(prev => ({ ...prev, [section]: [...(prev[section] || []), template] }));
  };

  const removeFromArr = (section, index) => {
    setData(prev => ({ ...prev, [section]: (prev[section] || []).filter((_, i) => i !== index) }));
  };

  const next = () => step < STEPS.length - 1 && setStep(step + 1);
  const prev = () => step > 0 && setStep(step - 1);

  const d = data;

  const renderStep = () => {
    switch (step) {
      case 0: return <Step1 d={d} u={u} />;
      case 1: return <Step2 d={d} u={u} />;
      case 2: return <Step3 d={d} u={u} />;
      case 3: return <Step4 d={d} u={u} />;
      case 4: return <Step5 d={d} u={u} />;
      case 5: return <Step6 d={d} u={u} />;
      case 6: return <Step7 d={d} u={u} />;
      case 7: return <Step8 d={d} u={u} uArr={uArr} addToArr={addToArr} removeFromArr={removeFromArr} />;
      case 8: return <Step9 d={d} u={u} />;
      case 9: return <Step10 d={d} u={u} />;
      case 10: return <Step11 d={d} u={u} />;
      case 11: return <Step13 d={d} u={u} />;
      case 12: return <Step15 d={d} u={u} uArr={uArr} addToArr={addToArr} removeFromArr={removeFromArr} />;
      case 13: return <StepReview d={d} onComplete={onComplete} />;
      default: return null;
    }
  };

  return (
    <div data-testid="intake-form">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#595959]">Step {step + 1} of {STEPS.length}</span>
          <span className="text-xs font-medium text-[#1C2E4A]">{STEPS[step]}</span>
        </div>
        <div className="w-full bg-[#E5E2DC] rounded-full h-2">
          <div className="bg-[#C8A97E] h-2 rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">{renderStep()}</div>

      {/* Navigation */}
      {step < 13 && (
        <div className="flex justify-between mt-8 pt-6 border-t border-[#E5E2DC]">
          <button data-testid="intake-prev-btn" onClick={prev} disabled={step === 0} className="flex items-center gap-2 text-sm text-[#595959] hover:text-[#1C2E4A] disabled:opacity-30 font-medium">
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button data-testid="intake-next-btn" onClick={next} className="flex items-center gap-2 bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2.5 rounded-md text-sm font-medium">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// STEP 1: Customer Information
function Step1({ d, u }) {
  const s = d.customer_info || {};
  const set = (f, v) => u("customer_info", f, v);
  return (<div><SectionTitle title="Customer Information" />
    <div className="bg-[#1C2E4A]/5 rounded-xl border border-[#1C2E4A]/10 p-5 mb-6">
      <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-2">Don't worry if you don't have all the answers yet!</h4>
      <p className="text-sm text-[#595959] leading-relaxed">
        Throughout this form, you'll see an <span className="text-[#C8A97E] font-medium">"I need help writing this"</span> option next to many fields. Check that box and we'll handle it for you — mission statements, school policies, refund policies, welcome letters, document wording, all of it. We can create everything for you so your school looks professional and established from day one, just like ours.
      </p>
    </div>
    <Field label="Full Legal Name *"><TextInput value={s.full_name} onChange={v => set("full_name", v)} placeholder="Your full legal name" required /></Field>
    <Field label="Preferred Name"><TextInput value={s.preferred_name} onChange={v => set("preferred_name", v)} placeholder="What should we call you?" /></Field>
    <Field label="Email Address *"><TextInput value={s.email} onChange={v => set("email", v)} placeholder="you@example.com" type="email" required /></Field>
    <Field label="Phone Number *"><TextInput value={s.phone} onChange={v => set("phone", v)} placeholder="(555) 555-5555" /></Field>
    <Field label="Mailing Address"><TextInput value={s.address} onChange={v => set("address", v)} placeholder="Street, City, State, ZIP" /></Field>
    <Field label="Preferred Contact Method">
      <select value={s.contact_method || ""} onChange={e => set("contact_method", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option>
      </select>
    </Field>
    <Field label="Additional Admin Access" helpText="Name and email of anyone else who should have admin access">
      <TextArea value={s.additional_admins} onChange={v => set("additional_admins", v)} placeholder="Name - email@example.com (one per line)" rows={2} />
    </Field>
  </div>);
}

// STEP 2: School Information
function Step2({ d, u }) {
  const s = d.school_info || {};
  const set = (f, v) => u("school_info", f, v);
  return (<div><SectionTitle title="Church School Information" />
    <Field label="Church School Name *"><TextInput value={s.school_name} onChange={v => set("school_name", v)} placeholder="e.g. Grace Christian Academy" /></Field>
    <Field label="Second-Choice School Name"><TextInput value={s.school_name_alt} onChange={v => set("school_name_alt", v)} placeholder="Alternative name if first is taken" /></Field>
    <Field label="Desired Opening Date"><TextInput value={s.opening_date} onChange={v => set("opening_date", v)} type="date" /></Field>
    <Field label="Year to List as Established"><TextInput value={s.established_year} onChange={v => set("established_year", v)} placeholder="e.g. 2025" /></Field>
    <Field label="School Administrator Name *"><TextInput value={s.admin_name} onChange={v => set("admin_name", v)} placeholder="Full name" /></Field>
    <Field label="School Administrator Title"><TextInput value={s.admin_title} onChange={v => set("admin_title", v)} placeholder="e.g. Director, Principal, Headmaster" /></Field>
    <Field label="Church or Ministry Name"><TextInput value={s.church_name} onChange={v => set("church_name", v)} placeholder="If applicable" /></Field>
    <Field label="Religious Affiliation or Denomination"><TextInput value={s.denomination} onChange={v => set("denomination", v)} placeholder="If applicable" /></Field>
    <Field label="School Phone Number"><TextInput value={s.school_phone} onChange={v => set("school_phone", v)} placeholder="(555) 555-5555" /></Field>
    <Field label="School Mailing Address"><TextInput value={s.school_address} onChange={v => set("school_address", v)} placeholder="Street, City, State, ZIP" /></Field>
    <Field label="Counties or Areas Served"><TextInput value={s.areas_served} onChange={v => set("areas_served", v)} placeholder="e.g. Houston County, Dale County" /></Field>
    <Field label="Accept families statewide?">
      <select value={s.statewide || ""} onChange={e => set("statewide", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
    <Field label="Grade Levels Accepted"><TextInput value={s.grade_levels} onChange={v => set("grade_levels", v)} placeholder="e.g. K-12" /></Field>
    <Field label="Accept kindergarten students?">
      <select value={s.kindergarten || ""} onChange={e => set("kindergarten", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
    <Field label="Short School Description"><TextArea value={s.description} onChange={v => set("description", v)} placeholder="A brief description of your school" /><HelpToggle label="I need help writing this" checked={s.description_help} onChange={v => set("description_help", v)} /></Field>
    <Field label="About Us Information"><TextArea value={s.about} onChange={v => set("about", v)} placeholder="Tell us about your school's story" /><HelpToggle label="I need help writing this" checked={s.about_help} onChange={v => set("about_help", v)} /></Field>
    <Field label="Mission Statement"><TextArea value={s.mission} onChange={v => set("mission", v)} placeholder="Your school's mission" /><HelpToggle label="I need help writing this" checked={s.mission_help} onChange={v => set("mission_help", v)} /></Field>
    <Field label="Motto or Tagline"><TextInput value={s.motto} onChange={v => set("motto", v)} placeholder="e.g. Faith. Family. Future." /><HelpToggle label="I need help writing this" checked={s.motto_help} onChange={v => set("motto_help", v)} /></Field>
  </div>);
}

// STEP 3: Domain & Email
function Step3({ d, u }) {
  const s = d.domain_info || {};
  const set = (f, v) => u("domain_info", f, v);
  return (<div><SectionTitle title="Domain & School Email" />
    <p className="text-xs text-[#595959] mb-4 bg-[#FAF9F6] p-3 rounded-lg border border-[#E5E2DC]">Domain names are subject to availability and will not be purchased, transferred, or changed without your approval. First year is included free; renewal is $25/year.</p>
    <Field label="Preferred Domain Name *"><TextInput value={s.domain1} onChange={v => set("domain1", v)} placeholder="e.g. gracechristianacademy.com" /></Field>
    <Field label="Second-Choice Domain"><TextInput value={s.domain2} onChange={v => set("domain2", v)} placeholder="Backup option" /></Field>
    <Field label="Third-Choice Domain"><TextInput value={s.domain3} onChange={v => set("domain3", v)} placeholder="Third backup" /></Field>
    <Field label="Do you already own a domain?">
      <select value={s.owns_domain || ""} onChange={e => set("owns_domain", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
    {s.owns_domain === "yes" && <>
      <Field label="Existing Domain Name"><TextInput value={s.existing_domain} onChange={v => set("existing_domain", v)} placeholder="yourdomain.com" /></Field>
      <Field label="Domain Registrar"><TextInput value={s.registrar} onChange={v => set("registrar", v)} placeholder="e.g. GoDaddy, Namecheap, HostGator" /></Field>
      <Field label="Need help connecting your domain?">
        <select value={s.domain_help || ""} onChange={e => set("domain_help", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
          <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
        </select>
      </Field>
    </>}
    <Field label="Preferred School Email *"><TextInput value={s.school_email} onChange={v => set("school_email", v)} placeholder="e.g. info@yourschool.com" type="email" /></Field>
    <Field label="Additional Email Addresses"><TextArea value={s.additional_emails} onChange={v => set("additional_emails", v)} placeholder="One per line" rows={2} /></Field>
  </div>);
}

// STEP 4: Branding
function Step4({ d, u }) {
  const s = d.branding || {};
  const set = (f, v) => u("branding", f, v);
  const styles = ["Traditional","Modern","Elegant","Family-friendly","Christian","Academic","Minimal","Colorful","Rustic","Other"];
  const symbols = ["Cross","Bible","Church","Dove","Shield or crest","Tree","Books","No religious symbols","Other"];
  return (<div><SectionTitle title="Branding" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Field label="Primary Color"><TextInput value={s.primary_color} onChange={v => set("primary_color", v)} placeholder="e.g. Navy blue, #1C2E4A" /></Field>
      <Field label="Secondary Color"><TextInput value={s.secondary_color} onChange={v => set("secondary_color", v)} placeholder="e.g. Gold" /></Field>
      <Field label="Accent Color"><TextInput value={s.accent_color} onChange={v => set("accent_color", v)} placeholder="e.g. White" /></Field>
    </div>
    <Field label="Colors to Avoid"><TextInput value={s.avoid_colors} onChange={v => set("avoid_colors", v)} placeholder="Any colors you don't want" /></Field>
    <Field label="Preferred Design Style">
      <div className="flex flex-wrap gap-2 mt-1">
        {styles.map(st => (
          <button key={st} type="button" onClick={() => set("design_style", st)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${s.design_style === st ? "bg-[#C8A97E] text-white border-[#C8A97E]" : "border-[#E5E2DC] text-[#595959] hover:border-[#C8A97E]"}`}>{st}</button>
        ))}
      </div>
    </Field>
    {s.design_style === "Other" && <Field label="Describe your style"><TextInput value={s.design_style_other} onChange={v => set("design_style_other", v)} placeholder="Describe what you're looking for" /></Field>}
    <Field label="Do you have a logo to upload?" helpText="You can email your logo to us after submitting">
      <select value={s.has_logo || ""} onChange={e => set("has_logo", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes, I'll email it</option><option value="no">No, I need one created</option>
      </select>
    </Field>
    <Field label="Logo Symbols or Imagery Preferences"><TextInput value={s.logo_symbols} onChange={v => set("logo_symbols", v)} placeholder="e.g. Cross with an open book" /></Field>
    <Field label="Symbols to Avoid"><TextInput value={s.avoid_symbols} onChange={v => set("avoid_symbols", v)} placeholder="Anything you don't want" /></Field>
    <Field label="Include Religious Symbols?">
      <select value={s.religious_symbols || ""} onChange={e => set("religious_symbols", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
    {s.religious_symbols === "yes" && <Field label="Religious Symbol Preference">
      <div className="flex flex-wrap gap-2 mt-1">
        {symbols.map(sym => (
          <button key={sym} type="button" onClick={() => set("symbol_pref", sym)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all ${s.symbol_pref === sym ? "bg-[#C8A97E] text-white border-[#C8A97E]" : "border-[#E5E2DC] text-[#595959] hover:border-[#C8A97E]"}`}>{sym}</button>
        ))}
      </div>
    </Field>}
    <Field label="Links to Websites You Like"><TextArea value={s.inspiration_links} onChange={v => set("inspiration_links", v)} placeholder="Paste URLs of websites you like (one per line)" rows={2} /></Field>
    <Field label="What Do You Like About Those Sites?"><TextArea value={s.inspiration_notes} onChange={v => set("inspiration_notes", v)} placeholder="Colors? Layout? Feel?" rows={2} /></Field>
  </div>);
}

// STEP 5: School Pricing
function Step5({ d, u }) {
  const s = d.school_pricing || {};
  const set = (f, v) => u("school_pricing", f, v);
  return (<div><SectionTitle title="School Pricing" />
    <p className="text-xs text-[#595959] mb-4">Enter "Not decided yet" for any pricing you haven't finalized. This can be updated anytime from your admin portal — nothing here is permanent.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="New Family Enrollment Fee"><TextInput value={s.enrollment_fee} onChange={v => set("enrollment_fee", v)} placeholder="e.g. $149/year" /></Field>
      <Field label="Annual Re-enrollment Fee"><TextInput value={s.reenroll_fee} onChange={v => set("reenroll_fee", v)} placeholder="e.g. $149/year" /></Field>
      <Field label="Transcript Fee"><TextInput value={s.transcript_fee} onChange={v => set("transcript_fee", v)} placeholder="e.g. $50" /></Field>
      <Field label="Diploma Fee"><TextInput value={s.diploma_fee} onChange={v => set("diploma_fee", v)} placeholder="e.g. $75" /></Field>
      <Field label="Report Card Fee"><TextInput value={s.report_card_fee} onChange={v => set("report_card_fee", v)} placeholder="e.g. $25" /></Field>
      <Field label="Enrollment Verification Letter Fee"><TextInput value={s.verification_fee} onChange={v => set("verification_fee", v)} placeholder="e.g. $15" /></Field>
      <Field label="Graduation Package Fee"><TextInput value={s.graduation_fee} onChange={v => set("graduation_fee", v)} placeholder="e.g. $100" /></Field>
    </div>
    <Field label="Multi-year Re-enrollment Pricing (1-5 years)"><TextArea value={s.multiyear_pricing} onChange={v => set("multiyear_pricing", v)} placeholder="e.g. 2 years: $275, 3 years: $400..." rows={3} /></Field>
    <Field label="Other Service Fees"><TextArea value={s.other_fees} onChange={v => set("other_fees", v)} placeholder="Any additional fees" rows={2} /></Field>
    <Field label="Accept manual payments?">
      <select value={s.manual_payments || ""} onChange={e => set("manual_payments", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
    {s.manual_payments === "yes" && <Field label="Manual Payment Methods"><TextInput value={s.manual_methods} onChange={v => set("manual_methods", v)} placeholder="e.g. Cash App, Zelle, Check" /></Field>}
    <Field label="Refund Policy"><TextArea value={s.refund_policy} onChange={v => set("refund_policy", v)} placeholder="Describe your refund policy" rows={3} /><HelpToggle label="I need help writing this" checked={s.refund_help} onChange={v => set("refund_help", v)} /></Field>
  </div>);
}

// STEP 6: School Year
function Step6({ d, u }) {
  const s = d.school_year || {};
  const set = (f, v) => u("school_year", f, v);
  return (<div><SectionTitle title="School Year & Enrollment" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Enrollment Opening Date"><TextInput value={s.enroll_open} onChange={v => set("enroll_open", v)} type="date" /></Field>
      <Field label="Enrollment Closing Date"><TextInput value={s.enroll_close} onChange={v => set("enroll_close", v)} type="date" /></Field>
      <Field label="School Year Start"><TextInput value={s.year_start} onChange={v => set("year_start", v)} type="date" /></Field>
      <Field label="School Year End"><TextInput value={s.year_end} onChange={v => set("year_end", v)} type="date" /></Field>
      <Field label="Re-enrollment Deadline"><TextInput value={s.reenroll_deadline} onChange={v => set("reenroll_deadline", v)} type="date" /></Field>
    </div>
    <Field label="Enrollment Eligibility Requirements"><TextArea value={s.eligibility} onChange={v => set("eligibility", v)} placeholder="Any requirements to enroll?" rows={2} /></Field>
    <Field label="Required Enrollment Documents"><TextArea value={s.required_docs} onChange={v => set("required_docs", v)} placeholder="e.g. Birth certificate, immunization records" rows={2} /></Field>
    <Field label="Does enrollment require admin approval?">
      <select value={s.approval_required || ""} onChange={e => set("approval_required", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No, auto-approve</option>
      </select>
    </Field>
    <Field label="What happens after enrollment is submitted?"><TextArea value={s.post_enrollment} onChange={v => set("post_enrollment", v)} placeholder="Describe the process after a family enrolls" rows={2} /></Field>
    <Field label="Enrollment Confirmation Email Content"><TextArea value={s.confirmation_email} onChange={v => set("confirmation_email", v)} placeholder="What should the confirmation email say?" rows={2} /><HelpToggle label="I need help writing this" checked={s.confirmation_help} onChange={v => set("confirmation_help", v)} /></Field>
  </div>);
}

// STEP 7: Enrollment Form Fields
function Step7({ d, u }) {
  const s = d.enrollment_fields || {};
  const set = (f, v) => u("enrollment_fields", f, v);
  const fields = [
    "Parent/guardian name","Parent/guardian email","Parent/guardian phone","Parent/guardian address",
    "Student legal name","Student preferred name","Student date of birth","Student grade level",
    "Student gender","Student address","County","Previous school","Withdrawal date",
    "Emergency contact","Custody information","BOE/superintendent info","Curriculum choice",
    "Attendance agreement","School policy agreement","Digital signature","Document uploads",
    "Multiple students per family","Additional custom questions"
  ];
  return (<div><SectionTitle title="Enrollment Form Information" />
    <p className="text-xs text-[#595959] mb-4">Select the information your school will collect during enrollment.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {fields.map(f => (
        <div key={f} className="flex items-center gap-2.5 py-1.5">
          <Checkbox checked={(s.selected_fields || []).includes(f)} onCheckedChange={checked => {
            const current = s.selected_fields || [];
            set("selected_fields", checked ? [...current, f] : current.filter(x => x !== f));
          }} />
          <span className="text-sm text-[#595959]">{f}</span>
        </div>
      ))}
    </div>
    <Field label="Additional Custom Questions"><TextArea value={s.custom_questions} onChange={v => set("custom_questions", v)} placeholder="Any additional questions you want on the enrollment form?" rows={3} /></Field>
  </div>);
}

// STEP 8: Policies
function Step8({ d, u }) {
  const s = d.policies || {};
  const set = (f, v) => u("policies", f, v);
  const policies = [
    ["attendance", "Attendance Requirements"], ["recordkeeping", "Parent Recordkeeping Responsibilities"],
    ["curriculum", "Curriculum Requirements"], ["testing", "Testing Requirements"],
    ["grade_reporting", "Grade Reporting Requirements"], ["promotion", "Promotion Requirements"],
    ["graduation", "Graduation Requirements"], ["transcript", "Transcript Procedures"],
    ["diploma", "Diploma Procedures"], ["withdrawal", "Withdrawal Procedures"],
    ["reenrollment", "Re-enrollment Procedures"], ["communication", "Communication Policy"],
    ["religious", "Religious Requirements"], ["record_retention", "Record Retention Procedures"],
    ["closure", "School Closure & Record Transfer"]
  ];
  return (<div><SectionTitle title="School Policies" />
    <p className="text-xs text-[#595959] mb-4">For each policy, enter details, request help, or mark as not applicable.</p>
    <div className="space-y-4">
      {policies.map(([key, label]) => (
        <div key={key} className="bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
          <Label className="text-sm font-medium text-[#1C2E4A] mb-2 block">{label}</Label>
          <TextArea value={s[key] || ""} onChange={v => set(key, v)} placeholder={`Enter ${label.toLowerCase()} or type 'N/A'`} rows={2} />
          <HelpToggle label="I need help writing this" checked={s[`${key}_help`]} onChange={v => set(`${key}_help`, v)} />
        </div>
      ))}
    </div>
  </div>);
}

// STEP 9: Document Info
function Step9({ d, u }) {
  const s = d.document_info || {};
  const set = (f, v) => u("document_info", f, v);
  return (<div><SectionTitle title="School Document Information" />
    <Field label="School Letterhead Name"><TextInput value={s.letterhead_name} onChange={v => set("letterhead_name", v)} placeholder="Name as it should appear on documents" /></Field>
    <Field label="School Mailing Address (for documents)"><TextInput value={s.doc_address} onChange={v => set("doc_address", v)} placeholder="Address on official documents" /></Field>
    <Field label="School Phone (for documents)"><TextInput value={s.doc_phone} onChange={v => set("doc_phone", v)} /></Field>
    <Field label="School Email (for documents)"><TextInput value={s.doc_email} onChange={v => set("doc_email", v)} /></Field>
    <Field label="Administrator Name (for signatures)"><TextInput value={s.sig_admin} onChange={v => set("sig_admin", v)} /></Field>
    <Field label="Administrator Title"><TextInput value={s.sig_title} onChange={v => set("sig_title", v)} /></Field>
    <Field label="BOE Notification Instructions"><TextArea value={s.boe_instructions} onChange={v => set("boe_instructions", v)} placeholder="Instructions for parents to notify their Board of Education" rows={3} /><HelpToggle label="I need help writing this" checked={s.boe_help} onChange={v => set("boe_help", v)} /></Field>
    <Field label="Address for Previous School Records"><TextArea value={s.records_address} onChange={v => set("records_address", v)} placeholder="Where previous schools should send student records (mail + email)" rows={2} /></Field>
    <Field label="Welcome Letter Wording"><TextArea value={s.welcome_wording} onChange={v => set("welcome_wording", v)} placeholder="What should the welcome letter say?" rows={3} /><HelpToggle label="I need help writing this" checked={s.welcome_help} onChange={v => set("welcome_help", v)} /></Field>
    <Field label="Additional Document Instructions"><TextArea value={s.additional_instructions} onChange={v => set("additional_instructions", v)} placeholder="Any other instructions for documents" rows={2} /></Field>
  </div>);
}

// STEP 10: Uploads
function Step10({ d, u }) {
  const s = d.uploads || {};
  const set = (f, v) => u("uploads", f, v);
  return (<div><SectionTitle title="Document Uploads" />
    <p className="text-xs text-[#595959] mb-4 bg-[#FAF9F6] p-3 rounded-lg border border-[#E5E2DC]">You can email documents to us after submitting this form at <span className="font-medium text-[#1C2E4A]">acsvirtualchurch@gmail.com</span>. Just list what you plan to send below. <span className="font-medium text-[#1C2E4A]">This section is completely optional</span> — if you don't have any documents ready, just skip ahead.</p>
    {["Logo","School handbook","Enrollment agreement","Attendance policy","Refund policy","Tuition information","Graduation requirements","Existing enrollment form","Transcript request form","Diploma request form","Church or ministry documentation","Staff photos","Website images","Other documents"].map(doc => (
      <div key={doc} className="flex items-center gap-2.5 py-1.5">
        <Checkbox checked={(s.planned_uploads || []).includes(doc)} onCheckedChange={checked => {
          const current = s.planned_uploads || [];
          set("planned_uploads", checked ? [...current, doc] : current.filter(x => x !== doc));
        }} />
        <span className="text-sm text-[#595959]">{doc}</span>
      </div>
    ))}
    <Field label="Notes About Uploads"><TextArea value={s.upload_notes} onChange={v => set("upload_notes", v)} placeholder="Anything we should know about the documents you'll send?" rows={2} /></Field>
  </div>);
}

// STEP 11: Website Content
function Step11({ d, u }) {
  const s = d.website_content || {};
  const set = (f, v) => u("website_content", f, v);
  const fields = [
    ["headline", "Homepage Headline"], ["intro", "Homepage Introduction"],
    ["about", "About Us Content"], ["enrollment_instructions", "Enrollment Instructions"],
    ["tuition_info", "Tuition & Fee Information"], ["faq", "Frequently Asked Questions"],
    ["contact_info", "Contact Information"], ["graduation_info", "Graduation Information"],
    ["church_info", "Church or Ministry Information"],
    ["refund_info", "Refund Policy"], ["additional", "Additional Website Content"]
  ];
  return (<div><SectionTitle title="Website Content" />
    {fields.map(([key, label]) => (
      <div key={key} className="mb-4">
        <Field label={label}>
          <TextArea value={s[key] || ""} onChange={v => set(key, v)} placeholder={`Enter ${label.toLowerCase()}`} rows={3} />
          <HelpToggle label="I need help writing this" checked={s[`${key}_help`]} onChange={v => set(`${key}_help`, v)} />
        </Field>
      </div>
    ))}
  </div>);
}

// STEP 12: Store
function Step12({ d, u }) {
  const s = d.store || {};
  const set = (f, v) => u("store", f, v);
  return (<div><SectionTitle title="Store Information" />
    <p className="text-xs text-[#595959] mb-4">A school store allows you to sell products and services like transcripts, diplomas, verification letters, and more. The store is available as an add-on and can be set up with your products after your site is built.</p>
    <Field label="Do you plan to sell additional products or services through your school?">
      <select value={s.has_store || ""} onChange={e => set("has_store", e.target.value)} className="w-full border border-[#E5E2DC] bg-[#FAF9F6] rounded-md px-3 py-2 text-sm">
        <option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option><option value="undecided">Not decided yet</option>
      </select>
    </Field>
    {s.has_store === "yes" && (
      <Field label="What products or services do you want to offer?" helpText="Just give us a general idea — we'll set everything up for you after your site is built.">
        <TextArea value={s.product_ideas} onChange={v => set("product_ideas", v)} placeholder="e.g. Transcripts, diplomas, report cards, enrollment verification letters, graduation packages..." rows={3} />
      </Field>
    )}
  </div>);
}

// STEP 13: Emails
function Step13({ d, u }) {
  const s = d.emails || {};
  const set = (f, v) => u("emails", f, v);
  const emails = [
    ["enrollment_confirm", "Enrollment Confirmation"], ["payment_confirm", "Payment Confirmation"],
    ["welcome", "Welcome Email"], ["reenroll_confirm", "Re-enrollment Confirmation"],
    ["reenroll_reminder", "Re-enrollment Reminder"], ["unpaid_reminder", "Unpaid Enrollment Reminder"],
    ["missing_info", "Missing Information Request"], ["password_reset", "Password Reset"],
    ["transcript_confirm", "Transcript Request Confirmation"], ["records_confirm", "Records Request Confirmation"],
    ["support_confirm", "Support Ticket Confirmation"]
  ];
  return (<div><SectionTitle title="Emails & Automated Messages" />
    <p className="text-xs text-[#595959] mb-4">For each email, enter your own wording, or let us write it for you.</p>
    {emails.map(([key, label]) => (
      <div key={key} className="mb-4 bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4">
        <Label className="text-sm font-medium text-[#1C2E4A] mb-2 block">{label}</Label>
        <TextArea value={s[key] || ""} onChange={v => set(key, v)} placeholder={`Custom wording for ${label.toLowerCase()}`} rows={2} />
        <HelpToggle label="Use default template / I need help" checked={s[`${key}_help`]} onChange={v => set(`${key}_help`, v)} />
      </div>
    ))}
  </div>);
}

// STEP 14: Support Chat
function Step14({ d, u }) {
  const s = d.support_chat || {};
  const set = (f, v) => u("support_chat", f, v);
  return (<div><SectionTitle title="Support Chat" />
    <Field label="School Support Hours"><TextInput value={s.hours} onChange={v => set("hours", v)} placeholder="e.g. Mon-Fri 9am-5pm CST" /></Field>
    <Field label="Support Email"><TextInput value={s.email} onChange={v => set("email", v)} placeholder="support@yourschool.com" type="email" /></Field>
    <Field label="Support Phone"><TextInput value={s.phone} onChange={v => set("phone", v)} placeholder="(555) 555-5555" /></Field>
    <Field label="Common Questions the Chat Should Answer"><TextArea value={s.common_questions} onChange={v => set("common_questions", v)} placeholder="List questions and answers the chat should handle" rows={4} /></Field>
    <Field label="Questions to Send Directly to You"><TextArea value={s.escalate_questions} onChange={v => set("escalate_questions", v)} placeholder="Topics that should create a support ticket instead" rows={2} /></Field>
    <Field label="Facts the Chat Should Know"><TextArea value={s.facts} onChange={v => set("facts", v)} placeholder="School-specific facts (hours, fees, deadlines, etc.)" rows={3} /></Field>
    <Field label="Topics the Chat Should NOT Answer"><TextArea value={s.off_limits} onChange={v => set("off_limits", v)} placeholder="e.g. Legal advice, specific student info" rows={2} /></Field>
    <Field label="Chat Greeting Message"><TextInput value={s.greeting} onChange={v => set("greeting", v)} placeholder="e.g. Welcome! How can I help you today?" /></Field>
  </div>);
}

// STEP 15: Administrators
function Step15({ d, u, uArr, addToArr, removeFromArr }) {
  const admins = d.administrators || [];
  return (<div><SectionTitle title="Administrator Accounts" />
    {admins.map((a, i) => (
      <div key={i} className="bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4 mb-3">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-[#1C2E4A]">Admin {i + 1}</span>
          <button type="button" onClick={() => removeFromArr("administrators", i)} className="text-xs text-red-500">Remove</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs mb-1 block">Full Name</Label><TextInput value={a.name} onChange={v => uArr("administrators", i, "name", v)} /></div>
          <div><Label className="text-xs mb-1 block">Email</Label><TextInput value={a.email} onChange={v => uArr("administrators", i, "email", v)} type="email" /></div>
          <div><Label className="text-xs mb-1 block">Role</Label><TextInput value={a.role} onChange={v => uArr("administrators", i, "role", v)} placeholder="e.g. Super Admin, Admin" /></div>
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          {[["notify_enrollment","Enrollment notifications"],["notify_payment","Payment notifications"],["notify_support","Support notifications"],["full_access","Full admin access"]].map(([key, label]) => (
            <div key={key} className="flex items-center gap-2"><Checkbox checked={a[key] || false} onCheckedChange={v => uArr("administrators", i, key, v)} /><span className="text-xs text-[#595959]">{label}</span></div>
          ))}
        </div>
      </div>
    ))}
    <button type="button" onClick={() => addToArr("administrators", { name: "", email: "", role: "", notify_enrollment: true, notify_payment: true, notify_support: true, full_access: false })} className="text-sm text-[#C8A97E] font-medium">+ Add Administrator</button>
  </div>);
}



// STEP 16: Review & Submit
function StepReview({ d, onComplete }) {
  const [agreed, setAgreed] = useState({});
  const [signature, setSignature] = useState("");
  const agreements = [
    "The submitted information is accurate.",
    "I have permission to use all uploaded logos, images, documents, and content.",
    "Domain names are subject to availability.",
    "I am responsible for reviewing the school's legal obligations.",
    "The website and setup service do not provide legal advice.",
    "I am responsible for approving school policies before they are published.",
    "Additional work outside the purchased package may require an additional fee.",
    "The website will not be launched until I approve the final version.",
  ];
  const policyAgreements = [
    { key: "terms", label: "I have read and agree to the", linkText: "Terms of Service", href: "/terms#terms" },
    { key: "privacy", label: "I have read and agree to the", linkText: "Privacy Policy", href: "/terms#privacy" },
    { key: "refund", label: "I have read and agree to the", linkText: "No-Refund & Cancellation Policy (all sales are final)", href: "/terms#refund" },
  ];
  const allAgreed = agreements.every((_, i) => agreed[i]) && policyAgreements.every(p => agreed[p.key]) && signature.trim().length > 0;

  return (<div><SectionTitle title="Review & Submit" />
    <div className="bg-[#FAF9F6] rounded-lg border border-[#E5E2DC] p-4 mb-6">
      <p className="text-sm text-[#1C2E4A] font-medium mb-2">Summary</p>
      <p className="text-xs text-[#595959]"><b>Customer:</b> {d.customer_info?.full_name || "—"}</p>
      <p className="text-xs text-[#595959]"><b>Email:</b> {d.customer_info?.email || "—"}</p>
      <p className="text-xs text-[#595959]"><b>School:</b> {d.school_info?.school_name || "—"}</p>
      <p className="text-xs text-[#595959]"><b>Domain:</b> {d.domain_info?.domain1 || "—"}</p>
    </div>

    <div className="space-y-3 mb-6">
      {agreements.map((text, i) => (
        <div key={i} className="flex items-start gap-2.5">
          <Checkbox checked={agreed[i] || false} onCheckedChange={v => setAgreed(prev => ({ ...prev, [i]: v }))} />
          <span className="text-sm text-[#595959]">{text}</span>
        </div>
      ))}
      <div className="mt-2 pt-4 border-t border-[#E5E2DC] space-y-3">
        {policyAgreements.map((p) => (
          <div key={p.key} className="flex items-start gap-2.5">
            <Checkbox checked={agreed[p.key] || false} onCheckedChange={v => setAgreed(prev => ({ ...prev, [p.key]: v }))} data-testid={`agree-${p.key}`} />
            <span className="text-sm text-[#595959]">
              {p.label}{" "}
              <a href={p.href} target="_blank" rel="noopener noreferrer" className="text-[#C8A97E] underline hover:text-[#B3956A] font-medium">{p.linkText}</a>.
            </span>
          </div>
        ))}
      </div>
    </div>

    <Field label="Typed Signature *">
      <TextInput value={signature} onChange={setSignature} placeholder="Type your full legal name as signature" required />
    </Field>

    <button
      disabled={!allAgreed}
      onClick={() => onComplete(signature)}
      data-testid="intake-submit-btn"
      className="w-full bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-3.5 rounded-md transition-colors font-medium disabled:opacity-40 mt-4 text-base"
    >
      Submit & Proceed to Payment
    </button>
  </div>);
}
